import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { flowNodeColor } from 'fable/flowEditor';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import { PowerEmailFlowExtraDataT } from '../../PowerEmailFlow';
import { IntentDetectionModal } from '../../modals/intentDetection';

export type IntentDetectionNodeRef = {
  id: string;
  nextNodeId: string;
};

export type IntentDetectionPayload = {
  defaultNextNodeId: string;
  intentDetections: IntentDetectionNodeRef[];
};

type IntentDetectionNodeAttributes = GenericNodeAttributes<
  IntentDetectionPayload,
  PowerEmailFlowExtraDataT
>;
type IntentDetectionNodeProps = GenericNodeProps<
  IntentDetectionPayload,
  PowerEmailFlowExtraDataT
>;

const DEFAULT_LABEL = 'default';
const translatePayload: IntentDetectionNodeAttributes['translatePayload'] = (
  node,
  extraData,
) => {
  const {
    payload: { defaultNextNodeId, intentDetections },
  } = node;

  const intentDetectionConnections = Object.entries(intentDetections).map(
    ([key, { id, nextNodeId }]) => {
      const intent = extraData.intentDetections.find(
        (intent) => intent.id === id,
      );
      return {
        id: key,
        type: 'out',
        label: intent?.name || 'unknown',
        // adding the intent id to the connection label id to be able to update
        // the connection later on, because the label does not guarantee uniqueness
        labelId: intent?.id,
        connection: nextNodeId,
      };
    },
  );

  const defaultConnection = {
    id: uuid(),
    type: 'out',
    label: DEFAULT_LABEL,
    // the default branch is not bound to any intent
    labelId: undefined,
    connection: defaultNextNodeId || uuid(),
  };

  intentDetectionConnections.splice(0, 0, defaultConnection);
  return intentDetectionConnections;
};

const updateConnection: IntentDetectionNodeAttributes['updateConnection'] = (
  node,
  connection,
  connectionId,
) => {
  const { payload } = node;
  const { defaultNextNodeId, intentDetections } = payload;
  const connectionLabel = connection['connectionLabel'];
  const connectionLabelId = connection['connectionLabelId'];
  const intentDetection = intentDetections.find(
    (intent) => intent.id === connectionLabelId,
  );

  if (connectionLabel && intentDetection) {
    const newIntentDetection: IntentDetectionNodeRef = {
      id: intentDetection.id,
      nextNodeId: connectionId,
    };
    const index = intentDetections.findIndex(
      (intent) => intent.id === connectionLabelId,
    );
    intentDetections.splice(index, 1, newIntentDetection);
  }

  const newDefaultNextNodeId =
    connectionLabel === DEFAULT_LABEL ? connectionId : defaultNextNodeId;

  return {
    ...node,
    payload: {
      ...payload,
      intentDetections,
      defaultNextNodeId: newDefaultNextNodeId,
    },
  };
};

export const IntentDetectionAttributes: IntentDetectionNodeAttributes = {
  iconName: 'lightbulb',
  label: 'Intent detection',
  color: hexStringToNumber(flowNodeColor.purple),
  translatePayload,
  updateConnection,
  helpText: 'Use AI to classify the purpose and intention of inbound messages.',
  NodeContent: IntentDetectionNode,
  ActionModal: IntentDetectionModal,
};

export function IntentDetectionNode({
  node,
}: IntentDetectionNodeProps): JSX.Element {
  const { payload } = node;
  const { intentDetections } = payload;

  const optionCount = Object.keys(intentDetections).length;
  const optionText =
    optionCount === 1 ? 'Intent detection' : 'Intent detections';

  return (
    <TextContent>
      {useMemo(
        () => (
          <TextEllipsis>
            <Text weight="semibold" text={`${optionCount} ${optionText}`} />
          </TextEllipsis>
        ),
        [optionCount, optionText],
      )}
    </TextContent>
  );
}
